import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Input,
  DatePicker,
  Button,
  Tag,
  Typography,
  Card,
  Tooltip,
  message as antdMessage,
  Space,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";
import moment from "moment";

import { postReq } from "../api";

const { Text } = Typography;

/* ── small stat cell ─────────────────────────── */
const StatCell = ({ value, color }) =>
  value !== undefined && value !== null ? (
    <Tag color={color} style={{ minWidth: 32, textAlign: "center" }}>
      {value}
    </Tag>
  ) : (
    <Text type="secondary">—</Text>
  );

export default function AttendanceSummaryTable() {
  const [selectedMonth, setSelectedMonth] = useState(
    moment().subtract(1, "month"),
  );
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [rerunId, setRerunId] = useState(null); // tracks which row is rerunning
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10 });

  /* ── fetch summary ─────────────────────────── */
  const fetchData = useCallback(
    async (pg = pagination, q = search, month = selectedMonth) => {
      setLoading(true);
      try {
        const data = await postReq("/user/MonthlyAttendanceSummary", {
          page: pg.page,
          pageSize: pg.pageSize,
          search: q,
          month: month.format("M"),
          year: month.format("YYYY"),
        });

        if (data.status === 200) {
          setRows(data.data.data);
          setTotal(data.data.total);
        } else {
          antdMessage.error(data.message || "Failed to fetch data");
          setRows([]);
          setTotal(0);
        }
      } catch (err) {
        antdMessage.error(err?.message || "Error fetching data");
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    fetchData(pagination, search, selectedMonth);
  }, [pagination, search, selectedMonth]);

  /* ── rerun single user ─────────────────────── */
  const handleRerun = async (row) => {
    setRerunId(row._id);
    try {
      const { data } = await postReq("user/rerun_monthly_summary", {
        userId: row.userId,
        month: selectedMonth.format("M"),
        year: selectedMonth.format("YYYY"),
      });

      if (data.status === 200) {
        antdMessage.success(data.message || "Rerun successful");
        fetchData(pagination, search, selectedMonth);
      } else {
        antdMessage.error(data.message || "Rerun failed");
      }
    } catch (err) {
      antdMessage.error(err?.message || "Error");
    } finally {
      setRerunId(null);
    }
  };

  /* ── columns ───────────────────────────────── */
  const columns = [
    {
      title: "User",
      dataIndex: "userName",
      key: "userName",
      minWidth: 160,
      fixed: "left",
      render: (val) => (
        <Text strong style={{ color: "#13487D" }}>
          {val || "—"}
        </Text>
      ),
    },
    {
      title: "Month",
      dataIndex: "month",
      key: "month",
      width: 80,
      render: (val) => <Text>{val ?? "—"}</Text>,
    },
    {
      title: "Year",
      dataIndex: "year",
      key: "year",
      width: 80,
      render: (val) => <Text>{val ?? "—"}</Text>,
    },
    {
      title: "Present",
      dataIndex: "presentDays",
      key: "presentDays",
      width: 90,
      render: (val) => <StatCell value={val} color="success" />,
    },
    {
      title: "Half Days",
      dataIndex: "halfDays",
      key: "halfDays",
      width: 90,
      render: (val) => <StatCell value={val} color="warning" />,
    },
    {
      title: "Absent",
      dataIndex: "absentDays",
      key: "absentDays",
      width: 90,
      render: (val) => <StatCell value={val} color="error" />,
    },
    {
      title: "Paid Leave",
      dataIndex: "paidLeaveDays",
      key: "paidLeaveDays",
      width: 100,
      render: (val) => <StatCell value={val} color="blue" />,
    },
    {
      title: "Unpaid Leave",
      dataIndex: "unpaidLeaveDays",
      key: "unpaidLeaveDays",
      width: 110,
      render: (val) => <StatCell value={val} color="orange" />,
    },
    {
      title: "Payable Days",
      dataIndex: "payableDays",
      key: "payableDays",
      width: 110,
      render: (val) => <StatCell value={val} color="geekblue" />,
    },
    {
      title: "Working Days",
      dataIndex: "totalWorkingDays",
      key: "totalWorkingDays",
      width: 115,
      render: (val) => <StatCell value={val} color="cyan" />,
    },
    {
      title: "Calendar Days",
      dataIndex: "totalCalendarDays",
      key: "totalCalendarDays",
      width: 115,
      render: (val) => <StatCell value={val} color="purple" />,
    },
    {
      title: "Re-Run",
      key: "rerun",
      width: 80,
      fixed: "right",
      render: (_, row) => (
        <Tooltip title="Re-run attendance summary">
          <Button
            type="text"
            size="small"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            icon={
              <ReloadOutlined
                spin={rerunId === row._id}
                style={{ color: rerunId === row._id ? "#3699FF" : "#BAC7D6" }}
                size={16}
              />
            }
            loading={rerunId === row._id}
            onClick={() => handleRerun(row)}
          />
        </Tooltip>
      ),
    },
  ];

  /* ── render ─────────────────────────────────── */
  return (
    <Card
      style={{
        borderRadius: 12,
        boxShadow: "0 1px 4px rgba(19,72,125,0.08)",
        border: "1px solid #e8edf3",
      }}
      bodyStyle={{ padding: "24px 28px" }}
    >
      {/* Controls */}
      <div className="att-summary-controls">
        <Input
          prefix={<SearchOutlined style={{ color: "#BAC7D6" }} />}
          placeholder="Search user name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          style={{ maxWidth: 240, borderRadius: 8, borderColor: "#d0dae6" }}
        />

        <DatePicker
          picker="month"
          value={selectedMonth}
          disabledDate={(d) =>
            d && d.isAfter(moment().subtract(1, "month"), "month")
          }
          onChange={(val) => {
            if (val) {
              setSelectedMonth(val);
              setPagination((prev) => ({ ...prev, page: 1 }));
            }
          }}
          format="MMM YYYY"
          allowClear={false}
          style={{ width: 160, borderRadius: 8, borderColor: "#d0dae6" }}
          placeholder="Select month"
        />
      </div>

      {/* Table */}
      <Table
        dataSource={rows}
        columns={columns}
        rowKey={(r) => r._id}
        loading={loading}
        size="middle"
        scroll={{ x: 1200 }}
        style={{ marginTop: 20 }}
        pagination={{
          current: pagination.page,
          pageSize: pagination.pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ["5", "10", "20"],
          showTotal: (t) => `Total ${t} records`,
          onChange: (page, pageSize) => setPagination({ page, pageSize }),
        }}
        rowClassName={(_, i) => (i % 2 === 0 ? "att-summary-row-even" : "")}
      />

      <style>{`
        .att-summary-controls {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
          margin-bottom: 4px;
        }
        .att-summary-row-even td {
          background: #fafbfc !important;
        }
      `}</style>
    </Card>
  );
}
